import React, { useState } from "react";
import { DatePicker, Input, Form, Select, Switch, Button, TimePicker, Row, Col } from "antd";
import axios from "axios";
import { Error, Success } from "../Messages/Messages";
import { isAuthenticated } from "../Auth/auth";
import MainButtonComp from "../Buttons/MainButtonComp";
import GeneralModal from "../../serviceProvider/components/General.modal";
import { useEffect } from "react";
import { scheduleRowRange } from "./appointmentEvents";
import { sendAppointment } from "./appointmentApi";
import CustomerBranchPicker from "../Billing/CustomerBranchPicker";
import CustomerSelect from "../Billing/CustomerSelect";

const { RangePicker } = DatePicker;
import { Typography } from "antd";
import { useTranslation } from "react-i18next";

const AddAppointment = ({ updateParent, employeesArray }) => {
  const { t } = useTranslation("dashboard");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [clientsArray, setClientsArray] = useState([]);
  const [branchSel, setBranchSel] = useState({ clientBranch: null });
  const clientId = Form.useWatch("client", form);
  const selectedClient = clientsArray.find(c => String(c.value) === String(clientId))?.client;

  const { Title } = Typography;

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setBranchSel({ clientBranch: null });
  };

  const onSubmit = async rawValues => {
    const values = { ...rawValues, clientBranch: branchSel.clientBranch || null };
    let formData;
    if (values.multipleAppointments) {
      formData = {
        ...values,
        serviceProvider: isAuthenticated()?.serviceProviderId,
        appointmentSchedules: values.appointmentSchedules?.map(schedule => {
          // Also store the slot as combined instants (picked in the user's local time) so the
          // server never has to merge date + time in its own timezone when confirming.
          const range = scheduleRowRange(schedule);
          return {
            date: schedule.date.toISOString(),
            startTime: schedule.startTime.toISOString(),
            endTime: schedule.endTime.toISOString(),
            ...(range && !range.allDay
              ? { start: range.start.toISOString(), end: range.end.toISOString() }
              : {})
          };
        })
      };

      delete formData.dateRange;
    } else {
      formData = {
        ...values,
        serviceProvider: isAuthenticated()?.serviceProviderId,
        start: values.dateRange[0].toISOString(),
        end: values.dateRange[1].toISOString()
      };
    }

    try {
      const res = await sendAppointment("post", "/add", formData, t);
      if (!res) return; // the user kept the conflicts dialog's "Cancel"
      if (res.status === 200 || res.status === 304) {
        Success(res.data.successMessage);
        updateParent();
        handleCancel();
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      console.error(err);
      Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const getClients = async () => {
    setLoading(true);
    await axios
      .post(
        `${process.env.REACT_APP_BACKEND_URL}/users/clients/limited`,
        { pageSize: 100000000, pageNo: 0, serviceProvider: isAuthenticated()?.serviceProviderId },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      )
      .then(res => {
        setLoading(false);
        if (res.status === 200 || res.status === 304) {
          let makeOptions = res.data?.data?.map(d => ({
            label: [d?.company, `${d?.firstName || ""} ${d?.lastName || ""}`.trim()]
              .filter(Boolean)
              .join(" · "),
            value: d?._id,
            client: d
          }));
          setClientsArray(makeOptions);
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        setLoading(false);
        if (err.response?.data?.errorMessage) {
          Error(err.response?.data?.errorMessage);
        } else {
          Error(err.message);
        }
      });
  };

  useEffect(() => {
    getClients();

    return () => {};
  }, []);

  const items = (
    <Form
      form={form}
      layout="vertical"
      onFinish={onSubmit}
      initialValues={{
        title: "",
        dateRange: null,
        employees: [],
        desc: "",
        multipleAppointments: false,
        appointmentSchedules: []
      }}
    >
      <Form.Item
        label={
          <Title level={5} className="modals-inputs-label">
            {t("Title")}
          </Title>
        }
        name="title"
        rules={[{ required: true, message: t("Please enter the appointment title") }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="multipleAppointments"
        label={t("Multiple Appointments")}
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>

      {}
      <Form.Item
        shouldUpdate={(prevValues, curValues) =>
          prevValues.multipleAppointments !== curValues.multipleAppointments
        }
      >
        {({ getFieldValue }) => {
          return getFieldValue("multipleAppointments") ? (
            <Form.List name="appointmentSchedules">
              {(fields, { add, remove }) => (
                <>
                  {fields?.map(field => (
                    <Row key={field.key} gutter={[16, 0]} align="top">
                      <Col xs={24} sm={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "date"]}
                          fieldKey={[field.fieldKey, "date"]}
                          rules={[{ required: true, message: t("select", { field: t("Date") }) }]}
                        >
                          <DatePicker
                            className="w-100"
                            placeholder={t("select", { field: t("Date") })}
                            format={"DD/MMM/YYYY"}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "startTime"]}
                          fieldKey={[field.fieldKey, "startTime"]}
                          rules={[
                            { required: true, message: t("select", { field: t("Start Time") }) }
                          ]}
                        >
                          <TimePicker
                            className="w-100"
                            format="HH:mm"
                            placeholder={t("Start Time")}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} sm={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "endTime"]}
                          fieldKey={[field.fieldKey, "endTime"]}
                          rules={[
                            { required: true, message: t("select", { field: t("End Time") }) }
                          ]}
                        >
                          <TimePicker
                            className="w-100"
                            format="HH:mm"
                            placeholder={t("End Time")}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={3}>
                        <Button type="link" danger block onClick={() => remove(field.name)}>
                          {t("Remove")}
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      {t("Add Appointment Schedule")}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          ) : (
            <Form.Item
              label={t("Start/End Date")}
              name="dateRange"
              rules={[
                {
                  required: true,
                  message: t("select", { field: t("Start Date") + " " + t("End Date") })
                }
              ]}
            >
              <RangePicker showTime style={{ width: "100%" }} format={"DD/MMM/YYYY HH:mm"} />
            </Form.Item>
          );
        }}
      </Form.Item>
      <Form.Item
        name="client"
        label={<span>{t("Client")}</span>}
        rules={[{ required: true, message: t("select", { field: t("client") }) }]}
      >
        <CustomerSelect
          customers={clientsArray.map(o => o.client)}
          branch={branchSel.clientBranch}
          onChange={(id, branchId) => setBranchSel({ clientBranch: branchId })}
        />
      </Form.Item>
      <CustomerBranchPicker
        customer={selectedClient}
        value={branchSel}
        onChange={setBranchSel}
        showBillTo={false}
        showBranchSelect={false}
      />
      <Form.Item
        label={
          <Title level={5} className="modals-inputs-label">
            {t("Employees")}
          </Title>
        }
        name="employees"
      >
        <Select
          mode="multiple"
          optionFilterProp="label"
          options={employeesArray}
          placeholder={t("enter", { field: t("Employees") })}
        />
      </Form.Item>

      <Form.Item
        label={
          <Title level={5} className="modals-inputs-label">
            {t("Description")}
          </Title>
        }
        name="desc"
      >
        <Input.TextArea rows={4} />
      </Form.Item>
    </Form>
  );

  return (
    <div className="addappointment">
      <MainButtonComp text="Create Appointment" addBtn onClick={showModal} />
      <GeneralModal
        title={t("Add Appointment")}
        open={isModalOpen}
        okText={t("Save")}
        onOk={() => form.submit()}
        onCancel={handleCancel}
        items={items}
      />
    </div>
  );
};

export default AddAppointment;
